import { useState, useEffect } from 'react';
import { convertSectoHHMM, convertHHMMtoSec, formatTimestampIso, getYear, luxonParseSQLDate, luxonCompareDateTimes } from '../../../../libs.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * WindowDrawer — Add/Edit check-in or rounds window settings.
 *
 * Props:
 *   open        — drawer visibility
 *   onClose     — close callback
 *   onSave      — save callback (receives formData object)
 *   saving      — boolean, disables buttons while saving
 *   editTarget  — null for add mode, window object for edit mode
 *   tabType     — 'checkin' | 'rounds' (shows # Checks for rounds)
 *   campusId    — current campus ID
 */
const WindowDrawer = ({ open, onClose, onSave, saving, editTarget, tabType, campusId }) => {
    const isEdit = !!editTarget;
    const isRounds = tabType === 'rounds';

    const [description, setDescription] = useState('');
    const [allDay, setAllDay] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [interval, setInterval_] = useState('1');
    const [generateAlarm, setGenerateAlarm] = useState(true);
    const [alarmTime, setAlarmTime] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [errors, setErrors] = useState({});

    // Reset form when drawer opens or target changes
    useEffect(() => {
        if (open) {
            if (editTarget) {
                setDescription(editTarget.description || '');
                // All Day = starts at midnight and runs the full 24h. The backend
                // stores this as (0, 86400); (0, 0) is also treated as all-day.
                const isAllDay = editTarget.window_start === 0
                    && (editTarget.window_end === 86400 || editTarget.window_end === 0);
                setAllDay(isAllDay);
                setStartTime(isAllDay ? '00:00' : convertSectoHHMM(editTarget.window_start));
                setEndTime(isAllDay ? '23:59' : convertSectoHHMM(editTarget.window_end));
                setInterval_(editTarget.interval != null ? String(editTarget.interval) : '1');
                setGenerateAlarm(editTarget.generate_alarm === 1);
                setAlarmTime(convertSectoHHMM(editTarget.alarm_at));
                // enabled = ignore_until in past means enabled
                const isEnabled = luxonCompareDateTimes(luxonParseSQLDate(editTarget.ignore_until), 'lt');
                setEnabled(isEnabled);
            } else {
                setDescription('');
                setAllDay(false);
                setStartTime('');
                setEndTime('');
                setInterval_('1');
                setGenerateAlarm(true);
                setAlarmTime('');
                setEnabled(true);
            }
            setErrors({});
        }
    }, [open, editTarget]);

    const descriptionPattern = /^[a-zA-Z0-9_ \-/]+$/;

    const validate = () => {
        const errs = {};

        if (!description.trim()) {
            errs.description = 'Description is required.';
        } else if (!descriptionPattern.test(description.trim())) {
            errs.description = 'Only letters, numbers, spaces, and - _ / are allowed.';
        }

        if (!allDay) {
            if (!startTime) errs.startTime = 'Start time is required.';
            if (!endTime) errs.endTime = 'End time is required.';
        }

        if (isRounds) {
            const intVal = parseInt(interval);
            if (!interval || isNaN(intVal) || intVal < 1 || intVal > 24) {
                errs.interval = 'Must be 1-24.';
            }
        }

        if (generateAlarm) {
            if (!alarmTime) {
                errs.alarmTime = 'Alarm time is required when alarms are enabled.';
            } else if (startTime && endTime) {
                const startSec = convertHHMMtoSec(startTime);
                const alarmSec = convertHHMMtoSec(alarmTime);
                const endSec = endTime === '23:59' ? 86400 : convertHHMMtoSec(endTime);
                if (!(startSec < alarmSec && alarmSec <= endSec)) {
                    errs.alarmTime = 'Alarm time must be after start and at or before end.';
                }
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const windowStart = allDay ? 0 : convertHHMMtoSec(startTime);
        const windowEnd = allDay ? 86400 : (endTime === '23:59' ? 86400 : convertHHMMtoSec(endTime));

        const formData = {
            description: description.trim(),
            window_start: windowStart,
            window_end: windowEnd,
            generate_alarm: generateAlarm ? 1 : 0,
            alarm_at: generateAlarm ? convertHHMMtoSec(alarmTime) : 0,
            campus_id: campusId,
            window_frequency_id: 1,
            // Enabled state is stored via ignore_until: a past timestamp means
            // enabled/active, a future timestamp means disabled/inactive. Sent on
            // both add and edit (new windows default to enabled). Mirrors the
            // Classic Portal edit logic (checked -> getYear(0), unchecked -> getYear(10)).
            ignore_until: enabled
                ? formatTimestampIso(getYear(0)[0])
                : formatTimestampIso(getYear(10)[0]),
        };

        if (isRounds) {
            formData.interval = parseInt(interval);
        }

        if (isEdit) {
            formData.window_id = editTarget.window_id;
        }

        onSave(formData);
    };

    const clearError = (field) => {
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title={<>{isEdit
                            ? `Edit ${isRounds ? 'Rounds' : 'Check-in'} Window`
                            : `Add ${isRounds ? 'Rounds' : 'Check-in'} Window`}</>}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving
                                ? (isEdit ? 'Saving...' : 'Adding...')
                                : (isEdit ? 'Save Changes' : 'Add Window')}
                        </button>
                    </>
                )}
            >
                {/* Description */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        Description <span className="dp-drawer__required">*</span>
                    </label>
                    <input
                        className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
                        type="text"
                        value={description}
                        placeholder="Enter window description"
                        onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
                    />
                    {errors.description ? <p className="dp-drawer__error">{errors.description}</p> : null}
                </div>

                {/* All Day toggle */}
                <div className="ws-toggle-row">
                    <span className="ws-toggle-label">All Day</span>
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={allDay}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setAllDay(checked);
                                if (checked) {
                                    setStartTime('00:00');
                                    setEndTime('23:59');
                                    clearError('startTime');
                                    clearError('endTime');
                                } else {
                                    setStartTime('');
                                    setEndTime('');
                                }
                            }}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                </div>

                {/* Start Time / End Time */}
                <div className="ws-time-row">
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Start Time {!allDay ? <span className="dp-drawer__required">*</span> : null}
                        </label>
                        <input
                            className={`dp-drawer__input${errors.startTime ? ' is-invalid' : ''}`}
                            type="time"
                            step="300"
                            value={startTime}
                            disabled={allDay}
                            onChange={(e) => { setStartTime(e.target.value); clearError('startTime'); }}
                        />
                        {errors.startTime ? <p className="dp-drawer__error">{errors.startTime}</p> : null}
                    </div>
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            End Time {!allDay ? <span className="dp-drawer__required">*</span> : null}
                        </label>
                        <input
                            className={`dp-drawer__input${errors.endTime ? ' is-invalid' : ''}`}
                            type="time"
                            step="300"
                            value={endTime}
                            disabled={allDay}
                            onChange={(e) => { setEndTime(e.target.value); clearError('endTime'); }}
                        />
                        {errors.endTime ? <p className="dp-drawer__error">{errors.endTime}</p> : null}
                    </div>
                </div>

                {/* # Checks (Rounds only) */}
                {isRounds ? (
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            # of Checks <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.interval ? ' is-invalid' : ''}`}
                            type="number"
                            min="1"
                            max="24"
                            step="1"
                            value={interval}
                            title="The number of checks for the time period that must be performed in a spaced out evenly manner"
                            onChange={(e) => { setInterval_(e.target.value); clearError('interval'); }}
                            onBlur={(e) => {
                                let v = parseInt(e.target.value);
                                if (isNaN(v) || v < 1) v = 1;
                                if (v > 24) v = 24;
                                setInterval_(String(v));
                            }}
                        />
                        {errors.interval ? <p className="dp-drawer__error">{errors.interval}</p> : null}
                    </div>
                ) : null}

                {/* Generate Alarms toggle */}
                <div className="ws-toggle-row">
                    <span className="ws-toggle-label">Generate Alarms</span>
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={generateAlarm}
                            onChange={(e) => {
                                setGenerateAlarm(e.target.checked);
                                if (!e.target.checked) {
                                    setAlarmTime('');
                                    clearError('alarmTime');
                                }
                            }}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                </div>

                {/* Alarm Time */}
                {generateAlarm ? (
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Alarm Time <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.alarmTime ? ' is-invalid' : ''}`}
                            type="time"
                            step="300"
                            value={alarmTime}
                            onChange={(e) => { setAlarmTime(e.target.value); clearError('alarmTime'); }}
                        />
                        {errors.alarmTime ? <p className="dp-drawer__error">{errors.alarmTime}</p> : null}
                    </div>
                ) : null}

                {/* Enabled toggle (edit mode only) */}
                {isEdit ? (
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Enabled</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>
                ) : null}
            </Drawer>
        </>
    );
};

export default WindowDrawer;
